import type { Move, Square } from 'chess.js';
import { Chess } from 'chess.js';
import { computed, ref, shallowRef } from 'vue';

const STARTING_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

export function useChessEngine(initialFen?: string) {
  const game = shallowRef(new Chess(initialFen || STARTING_FEN));
  const version = ref(0);

  function triggerUpdate() {
    version.value++;
  }

  const fen = computed(() => {
    version.value;
    return game.value.fen();
  });

  const turn = computed((): 'w' | 'b' => {
    version.value;
    return game.value.turn();
  });

  const isCheck = computed(() => {
    version.value;
    return game.value.isCheck();
  });

  const isCheckmate = computed(() => {
    version.value;
    return game.value.isCheckmate();
  });

  const isDraw = computed(() => {
    version.value;
    return game.value.isDraw();
  });

  const isStalemate = computed(() => {
    version.value;
    return game.value.isStalemate();
  });

  const isGameOver = computed(() => {
    version.value;
    return game.value.isGameOver();
  });

  const moveNumber = computed(() => {
    version.value;
    return game.value.moveNumber();
  });

  const board = computed(() => {
    version.value;
    return game.value.board();
  });

  const history = computed(() => {
    version.value;
    return game.value.history({ verbose: true });
  });

  function getLegalMoves(square: Square): Move[] {
    return game.value.moves({ square, verbose: true });
  }

  function makeMove(from: Square, to: Square, promotion?: string): Move | null {
    try {
      const move = game.value.move({ from, to, promotion });
      triggerUpdate();
      return move;
    } catch {
      return null;
    }
  }

  function loadFen(newFen: string): boolean {
    try {
      game.value.load(newFen);
      triggerUpdate();
      return true;
    } catch {
      return false;
    }
  }

  function reset(): void {
    game.value.reset();
    triggerUpdate();
  }

  function needsPromotion(from: Square, to: Square): boolean {
    const moves = game.value.moves({ square: from, verbose: true });
    return moves.some((m) => m.to === to && m.promotion);
  }

  function undoMove(): Move | null {
    const move = game.value.undo();
    if (move) triggerUpdate();
    return move;
  }

  return {
    game,
    fen,
    turn,
    isCheck,
    isCheckmate,
    isDraw,
    isStalemate,
    isGameOver,
    moveNumber,
    board,
    history,
    getLegalMoves,
    makeMove,
    loadFen,
    reset,
    needsPromotion,
    undoMove,
  };
}
